import { apiImportData } from "@/api/import-data.api.ts";
import { ApiErrorException } from "@/types/api.types.ts";
import {
  ApiImportDataType,
  ApiImportParse,
  ApiImportParseFieldPayload,
  ApiImportPayloadSave,
} from "@/types/import-data.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const importDataKeys = createQueryKeys("importData", {
  all: null,
  preview: (fileUuid: string) => ({
    queryKey: [fileUuid],
    queryFn: async () => {
      const result = await apiImportData.preview(fileUuid);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  parse: (importId: number) => ({
    queryKey: [{ importId }],
    queryFn: async () => {
      const result = await apiImportData.parse(importId);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  parseByType: (importId: number, importType: ApiImportDataType, tableId?: number | null) => ({
    queryKey: [{ importId, importType, tableId }],
    queryFn: async () => {
      const result = await apiImportData.parseByType(importId, importType, tableId);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  result: (importId: number) => ({
    queryKey: [importId],
    queryFn: async () => {
      const result = await apiImportData.result(importId);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  previewField: (importId: number, columnIndex: number) => ({
    queryKey: [importId, columnIndex],
    queryFn: async () => {
      const result = await apiImportData.previewField(importId, columnIndex);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
});

export const useImportDataAll = () => {
  return useQuery({
    queryKey: importDataKeys.all.queryKey,
    queryFn: async () => {
      const result = await apiImportData.getAll();
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
};

export const useImportDataPreview = (fileUuid: string | undefined) => {
  return useQuery({
    ...importDataKeys.preview(fileUuid ?? ""),
    enabled: !!fileUuid,
  });
};

export const useImportDataParse = (importId: number | undefined) => {
  return useQuery({
    ...importDataKeys.parse(importId ?? 0),
    enabled: !!importId,
    staleTime: 1000 * 60 * 60 * 24,
  });
};

export const useImportDataParseByType = (
  importId: number | undefined,
  importType: ApiImportDataType | undefined,
  tableId?: number | null
) => {
  return useQuery({
    ...importDataKeys.parseByType(importId ?? 0, importType ?? ApiImportDataType.employee, tableId),
    enabled: !!importId && !!importType,
    staleTime: 1000 * 60 * 60 * 24,
  });
};

export const useImportDataResult = (importId: number | undefined) => {
  return useQuery({
    ...importDataKeys.result(importId ?? 0),
    enabled: !!importId,
  });
};

export const useImportDataPreviewField = (
  importId: number | undefined,
  columnIndex: number | undefined
) => {
  return useQuery({
    ...importDataKeys.previewField(importId ?? 0, columnIndex ?? 0),
    enabled: importId !== undefined && columnIndex !== undefined,
  });
};

export const useImportDataSave = () => {
  return useMutation({
    mutationFn: async (payload: ApiImportPayloadSave) => {
      const result = await apiImportData.save(payload);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
};

export const useImportDataStart = () => {
  return useMutation({
    mutationFn: async (importId: number) => {
      const result = await apiImportData.start(importId);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
};

export const useImportDataDelete = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (importId: number) => {
      const result = await apiImportData.delete(importId);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: importDataKeys.all.queryKey });
    },
  });
};

export const useImportDataParseField = (
  importType: ApiImportDataType | undefined,
  tableId?: number | null
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: ApiImportParseFieldPayload) => {
      const result = await apiImportData.parseField(payload);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (data, variables) => {
      if (!importType) return;

      queryClient.setQueryData(
        importDataKeys.parseByType(variables.import_id, importType, tableId).queryKey,
        (oldData: ApiImportParse | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            columns: oldData.columns.map((col) =>
              col.column_index === data?.column_index ? { ...col, ...data } : col
            ),
          };
        }
      );
    },
  });
};
